import type {
  QualityGatesQueryParams,
  CompliancePRListQueryParams,
  QualityGatesPRsResponse,
  QualityGatePRDetails,
  QualityGatePR,
  RetriggerResponse,
  PRStatus,
  GateStatus
} from '../../types/quality-gates'
import { devtoolsApiClient } from '../api/devtoolsApiClient'
import { logger } from '../../utils/logger'

const API_BASE = '/quality-gates'

/**
 * Documentation URLs for each quality gate script
 * Mirrors platform-backend/apps/developer-tools/src/quality-gates/constants/documentation.ts
 */
const SCRIPT_DOCUMENTATION: Record<string, string> = {
  cross_service_import_check:
    'http://platform.docs/services/quality-gates/cross-service-import-checks/',
  eslint: 'http://platform.docs/services/quality-gates/eslint-check/',
  custom_check: 'http://platform.docs/services/quality-gates/custom-check/',
  vue_eslint_check: 'http://platform.docs/services/quality-gates/vue-eslint-check/',
  dependency_license_check:
    'http://platform.docs/services/quality-gates/dependency-license-check/',
  dependency_vulnerability_check:
    'http://platform.docs/services/quality-gates/dependency-vulnerability-check/',
  hardcoded_api_keys: 'http://platform.docs/services/quality-gates/hardcoded-api-keys/',
  internal_api_key_checks: 'http://platform.docs/services/quality-gates/internal-api-key-checks/',
  rbac_check: 'http://platform.docs/services/quality-gates/rbac-check/',
  redis_observability_quality_checks:
    'http://platform.docs/services/quality-gates/redis-observability-enforcement-quality-gate/',
  mongoose_v8_checks:
    'http://platform.docs/services/quality-gates/enforcing-mongoose-8-quality-gate/',
  mongo_schema_gsm_env_check: 'http://platform.docs/services/quality-gates/mongo-schema-gsm-env-check/',
  mongo_index_presence_check:
    'http://platform.docs/services/quality-gates/mongo-index-presence-check/'
}

export const qualityGatesService = {
  /**
   * Fetch list of available repositories for filter dropdown
   */
  async fetchRepositories(): Promise<string[]> {
    try {
      logger.log('Fetching repositories list')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await devtoolsApiClient.get<any>(`${API_BASE}/repositories`)

      // Handle different response structures
      let repositories: string[] = []
      if (Array.isArray(response)) {
        repositories = response
      } else if (response?.repositories && Array.isArray(response.repositories)) {
        repositories = response.repositories
      } else if (response?.data && Array.isArray(response.data)) {
        repositories = response.data
      }

      logger.log('Repositories fetched:', repositories.length)
      return repositories.sort()
    } catch (error) {
      logger.error('Failed to fetch repositories:', error)
      return []
    }
  },

  /**
   * Fetch list of available gates for filter dropdown
   */
  async fetchGates(): Promise<string[]> {
    try {
      logger.log('Fetching gates list')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await devtoolsApiClient.get<any>(`${API_BASE}/gates`)

      // Handle different response structures
      let gates: string[] = []
      if (Array.isArray(response)) {
        gates = response
      } else if (response?.gates && Array.isArray(response.gates)) {
        gates = response.gates
      } else if (response?.data && Array.isArray(response.data)) {
        gates = response.data
      }

      logger.log('Gates fetched:', gates.length)
      return gates.sort()
    } catch (error) {
      logger.error('Failed to fetch gates:', error)
      return []
    }
  },

  /**
   * Fetch paginated list of PRs with optional filters
   */
  async fetchPRs(params: QualityGatesQueryParams = {}): Promise<QualityGatesPRsResponse> {
    try {
      logger.log('Fetching quality gates PRs with params:', params)

      const queryParams: Record<string, string> = {}
      if (params.repository) queryParams.repository = params.repository
      if (params.status) queryParams.status = params.status
      if (params.gate) queryParams.failingGate = params.gate // Backend expects 'failingGate'
      if (params.limit) queryParams.limit = params.limit.toString()
      if (params.cursor) queryParams.cursor = params.cursor
      // Team filters for leaderboard drill-down
      if (params.team) queryParams.team = params.team
      if (params.subTeam) queryParams.subTeam = params.subTeam

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await devtoolsApiClient.get<any>(`${API_BASE}/prs`, queryParams)

      // Log the full response to debug the structure
      logger.log('Quality gates PRs raw response:', JSON.stringify(response, null, 2))

      // Handle different possible response structures
      // The API might return: { data: [...] }, { prs: [...] }, or directly [...]
      let prsData: QualityGatePR[] = []
      let paginationData = { total: 0, hasMore: false, nextCursor: undefined }

      if (Array.isArray(response)) {
        // Response is directly an array
        prsData = response
        paginationData.total = response.length
      } else if (response?.data && Array.isArray(response.data)) {
        // Response has { data: [...], pagination: {...} }
        prsData = response.data
        paginationData = response.pagination || paginationData
      } else if (response?.prs && Array.isArray(response.prs)) {
        // Response has { prs: [...], pagination: {...} }
        prsData = response.prs
        paginationData = response.pagination || paginationData
      } else if (response?.results && Array.isArray(response.results)) {
        // Response has { results: [...], pagination: {...} }
        prsData = response.results
        paginationData = response.pagination || paginationData
      }

      logger.log('Quality gates PRs parsed:', {
        count: prsData.length,
        hasMore: paginationData.hasMore
      })

      return {
        data: prsData,
        pagination: paginationData
      } as QualityGatesPRsResponse
    } catch (error) {
      logger.error('Failed to fetch quality gates PRs:', error)
      throw new Error('Failed to fetch quality gates results. Please try again.')
    }
  },

  /**
   * Fetch PRs for compliance drill-down flow.
   * Uses dedicated backend endpoint so existing PR list behavior stays unchanged.
   */
  async fetchCompliancePRs(
    params: CompliancePRListQueryParams,
  ): Promise<QualityGatesPRsResponse> {
    try {
      logger.log('Fetching compliance PRs with params:', params)

      const queryParams: Record<string, string> = {
        team: params.team
      }
      if (params.subTeam) queryParams.subTeam = params.subTeam
      if (params.qualityGate) queryParams.qualityGate = params.qualityGate
      if (params.timeRange) queryParams.timeRange = params.timeRange
      if (params.limit) queryParams.limit = params.limit.toString()
      if (params.cursor) queryParams.cursor = params.cursor

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await devtoolsApiClient.get<any>(`${API_BASE}/prs/compliance`, queryParams)

      const prsData: QualityGatePR[] = response?.prs || response?.data || []
      const paginationData = response?.pagination || {
        total: prsData.length,
        hasMore: false,
        nextCursor: undefined
      }

      return {
        data: prsData,
        pagination: paginationData
      }
    } catch (error) {
      logger.error('Failed to fetch compliance PRs:', error)
      throw new Error('Failed to fetch compliance PRs. Please try again.')
    }
  },

  /**
   * Fetch detailed results for a specific PR
   */
  async fetchPRDetails(repository: string, prNumber: number): Promise<QualityGatePRDetails> {
    try {
      logger.log('Fetching PR details:', { repository, prNumber })

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await devtoolsApiClient.get<any>(
        `${API_BASE}/prs/${encodeURIComponent(repository)}/${prNumber}`
      )

      // Log the full response to debug the structure
      logger.log('PR details raw response:', JSON.stringify(response, null, 2))

      // Handle different possible response structures
      // The API returns { pr: {...} } wrapper
      let prDetails: QualityGatePRDetails

      if (response?.pr && typeof response.pr === 'object') {
        // Response has { pr: {...} } wrapper
        prDetails = response.pr
      } else if (
        response?.data &&
        typeof response.data === 'object' &&
        !Array.isArray(response.data)
      ) {
        // Response has { data: {...} } wrapper
        prDetails = response.data
      } else if (response?.repository || response?.pr_number) {
        // Response is the PR details directly
        prDetails = response
      } else {
        // Fallback - use response as is
        prDetails = response
      }

      logger.log('PR details parsed:', {
        repository: prDetails?.repository,
        prNumber: prDetails?.pr_number,
        status: prDetails?.status,
        hasResults: !!prDetails?.results
      })

      return prDetails
    } catch (error) {
      logger.error('Failed to fetch PR details:', error)
      throw new Error('Failed to fetch PR details. Please try again.')
    }
  },

  /**
   * Re-trigger quality gates for a PR
   */
  async retriggerPR(repository: string, prNumber: number): Promise<RetriggerResponse> {
    try {
      logger.log('Re-triggering quality gates:', { repository, prNumber })

      const response = await devtoolsApiClient.post<RetriggerResponse>(
        `${API_BASE}/prs/${encodeURIComponent(repository)}/${prNumber}/retrigger`
      )

      logger.log('Re-trigger response:', response)

      return response
    } catch (error) {
      logger.error('Failed to re-trigger quality gates:', error)
      throw new Error('Failed to re-trigger quality gates. Please try again.')
    }
  },

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: string): string {
    try {
      const date = new Date(timestamp)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      })
    } catch {
      return timestamp
    }
  },

  /**
   * Format relative time (e.g., "2 hours ago")
   */
  formatRelativeTime(timestamp: string): string {
    try {
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    } catch {
      return timestamp
    }
  },

  /**
   * Get status color classes for TailwindCSS
   */
  getStatusColor(status: PRStatus | GateStatus): string {
    const colors: Record<string, string> = {
      pass: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
      success: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
      fail: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
      failure: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
      warning: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
    }
    return colors[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
  },

  /**
   * Get status border color for cards
   */
  getStatusBorderColor(status: PRStatus): string {
    const colors: Record<PRStatus, string> = {
      pass: 'border-green-500',
      fail: 'border-red-500',
      warning: 'border-yellow-500'
    }
    return colors[status] || 'border-gray-300'
  },

  /**
   * Get status icon name
   */
  getStatusIcon(status: PRStatus | GateStatus): string {
    const icons: Record<string, string> = {
      pass: '✓',
      success: '✓',
      fail: '✗',
      failure: '✗',
      warning: '⚠'
    }
    return icons[status] || '?'
  },

  /**
   * Generate GitHub file link for a violation
   */
  getViolationLink(repository: string, commitSha: string, file: string, line: number): string {
    return `https://github.com/GoHighLevel/${repository}/blob/${commitSha}/${file}#L${line}`
  },

  /**
   * Format gate name for display (e.g., "hardcoded_api_keys" -> "Hardcoded API Keys")
   */
  formatGateName(gateName: string): string {
    return gateName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  },

  /**
   * Get documentation URL for a quality gate script
   * Returns null if no documentation is available
   */
  getGateDocumentationUrl(scriptName: string): string | null {
    return SCRIPT_DOCUMENTATION[scriptName] || null
  },

  /**
   * Check if a gate has documentation available
   */
  hasGateDocumentation(scriptName: string): boolean {
    return scriptName in SCRIPT_DOCUMENTATION
  },

  /**
   * Parse Firestore timestamp to Date
   * Handles both { _seconds, _nanoseconds } format and ISO strings
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parseBlockDate(blockDate: any): Date | null {
    if (!blockDate) return null

    try {
      // Firestore timestamp format: { _seconds: number, _nanoseconds: number }
      if (blockDate._seconds !== undefined) {
        return new Date(blockDate._seconds * 1000)
      }
      // ISO string format
      if (typeof blockDate === 'string') {
        return new Date(blockDate)
      }
      // Already a Date
      if (blockDate instanceof Date) {
        return blockDate
      }
      return null
    } catch {
      return null
    }
  },

  /**
   * Format block date for display
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatBlockDate(blockDate: any): string {
    const date = this.parseBlockDate(blockDate)
    if (!date) return 'No block date'

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  },

  /**
   * Check if block date is in the past (currently blocking)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isBlockDatePast(blockDate: any): boolean {
    const date = this.parseBlockDate(blockDate)
    if (!date) return false
    return date.getTime() < Date.now()
  },

  /**
   * Get block date status text and color
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getBlockDateInfo(blockDate: any): { text: string; colorClass: string; isPast: boolean } {
    const date = this.parseBlockDate(blockDate)
    if (!date) {
      return {
        text: 'No block date',
        colorClass: 'text-gray-500 dark:text-gray-400',
        isPast: false
      }
    }

    const isPast = date.getTime() < Date.now()
    const formattedDate = this.formatBlockDate(blockDate)

    if (isPast) {
      return {
        text: `Blocking since ${formattedDate}`,
        colorClass: 'text-red-600 dark:text-red-400 font-medium',
        isPast: true
      }
    } else {
      return {
        text: `Blocks on ${formattedDate}`,
        colorClass: 'text-yellow-600 dark:text-yellow-400',
        isPast: false
      }
    }
  }
}
